interface ChartData {
  name: string;
  value: number;
}

const PIE_COLORS = [
  '#5B8FF9',
  '#5AD8A6',
  '#F6BD16',
  '#E86452',
  '#6DC8EC',
  '#945FB9',
  '#FF9845',
  '#1E9493',
  '#FF99C3',
  '#7DA9F9',
];

const BAR_COLORS = [
  '#A8C5F7',
  '#A3E4C0',
  '#FCE58C',
  '#F5A89A',
  '#B8A4DA',
  '#D6B3E8',
  '#9BD9CC',
  '#FBC48A',
  '#B5C8E6',
  '#C8D99B',
];

export function SimpleBarChart({ data, title }: { data: ChartData[]; title: string }) {
  const safeData = data || [];
  const maxValue = Math.max(...safeData.map(d => d.value), 1);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
      <h3 className="text-base font-medium text-slate-700 mb-4">{title}</h3>
      <div className="space-y-2.5">
        {safeData.map((item, index) => (
          <div key={item.name} className="flex items-center gap-3">
            <div className="w-32 text-xs text-slate-500 truncate shrink-0" title={item.name}>{item.name}</div>
            <div className="flex-1 h-5 bg-slate-100/80 rounded-md overflow-hidden">
              <div
                className="h-full rounded-md transition-all duration-700"
                style={{
                  width: `${(item.value / maxValue) * 100}%`,
                  backgroundColor: BAR_COLORS[index % BAR_COLORS.length],
                }}
              />
            </div>
            <div className="w-10 text-xs text-slate-600 text-right tabular-nums">{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SimplePieChart({ data, title }: { data: ChartData[]; title: string }) {
  const safeData = data || [];
  const total = safeData.reduce((sum, d) => sum + d.value, 0);

  const cx = 100;
  const cy = 100;
  const radius = 65;

  let cumulativeAngle = -90;

  const paths = safeData.map((item, index) => {
    const angle = total > 0 ? (item.value / total) * 360 : 0;
    const startAngle = cumulativeAngle;
    const endAngle = cumulativeAngle + angle;
    cumulativeAngle = endAngle;

    if (angle >= 359.999) {
      return (
        <circle
          key={item.name}
          cx={cx}
          cy={cy}
          r={radius}
          fill={PIE_COLORS[index % PIE_COLORS.length]}
          opacity={0.75}
        />
      );
    }

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = cx + radius * Math.cos(startRad);
    const y1 = cy + radius * Math.sin(startRad);
    const x2 = cx + radius * Math.cos(endRad);
    const y2 = cy + radius * Math.sin(endRad);

    const largeArcFlag = angle > 180 ? 1 : 0;

    const pathData = [
      `M ${cx} ${cy}`,
      `L ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      'Z',
    ].join(' ');

    return (
      <path
        key={item.name}
        d={pathData}
        fill={PIE_COLORS[index % PIE_COLORS.length]}
        opacity={0.75}
        stroke="#fff"
        strokeWidth="1.5"
      />
    );
  });

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
      <h3 className="text-base font-medium text-slate-700 mb-4">{title}</h3>
      <div className="flex items-center gap-5">
        <div className="relative w-40 h-40 shrink-0">
          <svg className="w-full h-full" viewBox="0 0 200 200">
            {total > 0 ? paths : <circle cx={cx} cy={cy} r={radius} fill="#E2E8F0" />}
          </svg>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-sm text-slate-500 tabular-nums">{total}</span>
          </div>
        </div>
        <div className="flex-1 space-y-1.5">
          {safeData.map((item, index) => (
            <div key={item.name} className="flex items-center gap-2">
              <div
                className="w-2.5 h-2.5 rounded-sm shrink-0"
                style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length], opacity: 0.85 }}
              />
              <span className="text-xs text-slate-500 truncate flex-1" title={item.name}>{item.name}</span>
              <span className="text-xs text-slate-600 tabular-nums whitespace-nowrap">
                {item.value} <span className="text-slate-400">({total > 0 ? ((item.value / total) * 100).toFixed(1) : '0.0'}%)</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}